import { memo, useMemo } from 'react';
import { BaseEdge, getBezierPath, type EdgeProps } from '@xyflow/react';
import { motion } from 'framer-motion';

/**
 * Gera um path SVG customizado para back-edges (ciclos).
 * Usa a distância vertical para calcular o deslocamento horizontal,
 * garantindo um arco amplo e consistente no mesmo lado.
 */
function buildBackEdgePath(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  side: 'left' | 'right',
) {
  const dir = side === 'left' ? -1 : 1;
  const vertDist = Math.abs(ty - sy);

  const horizOffset = Math.max(120, vertDist * 0.45);

  const outerX =
    (side === 'left' ? Math.min(sx, tx) : Math.max(sx, tx)) +
    dir * horizOffset;

  const path = `M ${sx},${sy} C ${outerX},${sy} ${outerX},${ty} ${tx},${ty}`;

  // Label no ponto médio do bezier: B(0.5) = 0.125*P0 + 0.375*P1 + 0.375*P2 + 0.125*P3
  const lx = 0.125 * sx + 0.375 * outerX + 0.375 * outerX + 0.125 * tx;
  const ly = 0.125 * sy + 0.375 * sy + 0.375 * ty + 0.125 * ty;

  return { path, labelX: lx, labelY: ly };
}

function AnimatedEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  label,
  data,
}: EdgeProps) {
  const color = (data as any)?.color || '#A9D342';
  const isBackEdge = !!(data as any)?.isBackEdge;
  const backEdgeSide = ((data as any)?.backEdgeSide as 'left' | 'right') || 'right';
  const description = (data as any)?.description as string | undefined;
  const metrics = (data as any)?.metrics as string | undefined;
  const parallelOffset = ((data as any)?.parallelOffset as number) || 0;

  const { edgePath, labelX, labelY } = useMemo(() => {
    if (isBackEdge) {
      const result = buildBackEdgePath(sourceX, sourceY, targetX, targetY, backEdgeSide);
      return { edgePath: result.path, labelX: result.labelX, labelY: result.labelY };
    }

    const [path, lx, ly] = getBezierPath({
      sourceX: sourceX + parallelOffset,
      sourceY,
      sourcePosition,
      targetX: targetX + parallelOffset,
      targetY,
      targetPosition,
    });
    return { edgePath: path, labelX: lx, labelY: ly };
  }, [isBackEdge, backEdgeSide, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, parallelOffset]);

  const hasLabel = label || description || metrics;
  const textContent = description || (label as string) || '';
  const estimatedWidth = Math.max(60, Math.min(300, textContent.length * 7 + 24));
  const labelWidth = description || metrics ? estimatedWidth : 32;
  const labelHeight = description && metrics ? 48 : description || metrics ? 32 : 24;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          strokeOpacity: 0.6,
        }}
      />

      <circle r="3" fill={color} filter={`drop-shadow(0 0 3px ${color})`}>
        <animateMotion dur="3s" repeatCount="indefinite" path={edgePath} />
      </circle>

      {hasLabel && (
        <foreignObject
          width={labelWidth}
          height={labelHeight}
          x={labelX - labelWidth / 2}
          y={labelY - labelHeight / 2}
          requiredExtensions="http://www.w3.org/1999/xhtml"
          style={{ overflow: 'visible' }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex h-full w-full items-center justify-center"
          >
            <div
              className="flex flex-col items-center gap-0.5 rounded-lg px-2.5 py-1 text-center"
              style={{
                backgroundColor: `${color}18`,
                border: `1px solid ${color}40`,
                backdropFilter: 'blur(6px)',
                boxShadow: `0 1px 4px ${color}15`,
              }}
            >
              {label && !description && !metrics && (
                <span
                  className="text-[10px] font-bold leading-none"
                  style={{ color }}
                >
                  {label as string}
                </span>
              )}
              {description && (
                <span
                  className="text-[9px] font-semibold leading-tight whitespace-nowrap"
                  style={{ color }}
                >
                  {description}
                </span>
              )}
              {metrics && (
                <span
                  className="text-[9px] font-bold leading-tight whitespace-nowrap"
                  style={{ color }}
                >
                  {metrics}
                </span>
              )}
            </div>
          </motion.div>
        </foreignObject>
      )}
    </>
  );
}

export const AnimatedEdge = memo(AnimatedEdgeComponent);
